import type { ShiftProductionFormPayload } from "../../../shared/shift-production-form/contracts";

const checklistKeys = [
  "TANK_AND_PIPING",
  "MOVING_PARTS",
  "GUARDS_SENSORS_SWITCHES",
  "MOLD",
  "REEL_PACKAGING_LABEL_BOX",
] as const;
const measurementKeys = [
  "ACTUAL_LINE_SPEED",
  "OVEN_TEMPERATURE",
  "MOLD_TEMPERATURE",
  "TUNNEL_TEMPERATURE",
  "UNIT_WEIGHT",
  "PRODUCT_MOISTURE",
  "AMBIENT_TEMPERATURE",
  "AMBIENT_HUMIDITY",
] as const;

const measurementUnits: Record<(typeof measurementKeys)[number], string> = {
  ACTUAL_LINE_SPEED: "adet/dakika",
  OVEN_TEMPERATURE: "°C",
  MOLD_TEMPERATURE: "°C",
  TUNNEL_TEMPERATURE: "°C",
  UNIT_WEIGHT: "g/adet",
  PRODUCT_MOISTURE: "%",
  AMBIENT_TEMPERATURE: "°C",
  AMBIENT_HUMIDITY: "% bağıl nem",
};

export function emptyShiftFormPayload(
  operatorName = "",
  now = new Date(),
): ShiftProductionFormPayload {
  const dateParts = new Intl.DateTimeFormat("en", {
    timeZone: "Europe/Istanbul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const datePart = (type: string) => dateParts.find(part => part.type === type)?.value;
  return {
    documentNo: null,
    publicationDate: null,
    revisionDate: null,
    revisionNo: null,
    operatorName,
    departmentName: null,
    lineName: null,
    productionDate: `${datePart("year")}-${datePart("month")}-${datePart("day")}`,
    shift: null,
    checklist: checklistKeys.map((key) => ({
      key,
      state: null,
      explanation: "",
    })),
    checklistExplanation: "",
    products: [emptyProduct()],
    generalExplanation: "",
    lateCompletionReason: null,
  };
}

export function emptyProduct(): ShiftProductionFormPayload["products"][number] {
  return {
    productionOccurred: true,
    noProductionReason: null,
    productName: "",
    productCode: null,
    workOrderNo: null,
    machineStartedAt: null,
    machineStartNotOccurredReason: null,
    firstProductAt: null,
    firstProductNotOccurredReason: null,
    delayed: false,
    delayReason: null,
    moldCount: null,
    moldSuitable: null,
    speedDeviationReason: null,
    postChangeMachineStartedAt: null,
    postChangeDelayReason: null,
    postChangeFirstProductAt: null,
    postChangeFirstProductDelayReason: null,
    measurements: measurementKeys.map((key) => ({
      key,
      state: null,
      value: null,
      unit: measurementUnits[key],
      measuredAt: null,
      pointOrMethod: null,
      missingReason: null,
      explanation: null,
    })),
    speedChanges: [],
    downtimes: [],
    productChanges: [],
    quantities: {
      scrapKg: null,
      animalFeedKg: null,
      reelWasteQuantity: null,
      boxWasteCount: null,
      cartonWasteCount: null,
      labelWasteCount: null,
      targetCarton: null,
      targetCount: null,
      targetKg: null,
      actualCarton: null,
      actualCount: null,
      actualKg: null,
      targetNotDefined: false,
      targetExplanation: null,
      wasteReason: null,
      wasteResponsibleDepartment: null,
    },
    explanation: "",
  };
}

export function ensureCurrentProductMeasurements(
  product: ShiftProductionFormPayload["products"][number],
) {
  const existingKeys = new Set(product.measurements.map((measurement) => measurement.key));
  const missingMeasurements = measurementKeys
    .filter((key) => !existingKeys.has(key))
    .map((key) => ({
      key,
      state: null,
      value: null,
      unit: measurementUnits[key],
      measuredAt: null,
      pointOrMethod: null,
      missingReason: null,
      explanation: null,
    }));
  return missingMeasurements.length > 0
    ? { ...product, measurements: [...product.measurements, ...missingMeasurements] }
    : product;
}
